/*
 * CprMeter — счёт компрессий грудной клетки для тренажёра СЛР курса
 * «Первая помощь». Чистая логика: ни DOM, ни событий браузера — та же
 * функция считает на странице и в tests/js/first-aid-cpr-meter.test.mjs.
 *
 * Два входа, одна оценка:
 *   • датчик — сэмплы акселерометра (телефон лежит на подушке, его давят
 *     ладонями). Колебание ищет тот же детектор, что у /pedometer
 *     (PedometerSteps, public/js/pedometer/step-detector.js): модуль ускорения,
 *     полоса 0.6–3.5 Гц, триггер Шмитта, метки по времени, а не по номеру
 *     сэмпла. Компрессия — это то же «колебание», только ровнее и сильнее
 *     шага: подтверждение поезда — 3 подряд вместо 7, порог выше
 *     (замеры — docs/why/first-aid-course.md);
 *   • нажатие — тап по экрану или пробел (компьютер, телефон без датчика).
 *
 * Глубину компрессии телефон НЕ меряет: двойное интегрирование ускорения
 * плывёт на сантиметры за секунду, а подушка гасит ход по-разному. Оценка —
 * только темп (рекомендация ERC 2021: 100–120 в минуту) и ровность ритма.
 */
(function (root, factory) {
    var api = factory(root);
    if (typeof window !== 'undefined') window.CprMeter = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(this, function (root) {
    'use strict';

    var TARGET = [100, 120];
    /* Настройка детектора шагов под компрессии. Числа — из замера на
       синтетических трассах (tests/js/first-aid-cpr-meter.test.mjs). */
    var TUNE = { hMin: 0.3, maxGap: 1.6, confirm: 3, tol: 0.35 };
    /* Телефон лежит, а не в руке: гравитация почти вся по оси экрана (z).
       Ходьба с телефоном в руке — то же ровное колебание ~1.8 Гц, что и
       компрессии; без этого условия она считалась «компрессиями в зоне». */
    var FLAT = 0.8;          // |g_z| / |g| — до ~35° наклона
    var GRAV_HZ = 0.5;       // фильтр направления гравитации

    function median(a) {
        if (!a.length) return 0;
        var s = a.slice().sort(function (x, y) { return x - y; });
        var m = s.length >> 1;
        return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    }

    function create(opts) {
        opts = opts || {};
        var target = opts.target || TARGET;
        var onCompression = typeof opts.onCompression === 'function' ? opts.onCompression : null;
        var Steps = opts.detector || (root && root.PedometerSteps) || null;
        var times = [];     // метки компрессий, с
        var marks = [];     // для каждой компрессии: темп по трём последним интервалам (0 — ещё рано)
        var det = null;
        var gx = 0, gy = 0, gz = 0, gT = null, flat = true;

        function add(t) {
            if (times.length && t - times[times.length - 1] < 0.2) return; // дребезг: > 300/мин не бывает
            times.push(t);
            var n = times.length;
            var rate = 0;
            if (n >= 3) {
                var iv = [];
                for (var i = Math.max(1, n - 3); i < n; i++) iv.push(times[i] - times[i - 1]);
                var m = median(iv);
                rate = m > 0 ? 60 / m : 0;
            }
            marks.push(rate);
            if (onCompression) {
                try { onCompression({ n: n, t: t, rate: rate }); } catch (e) {}
            }
        }

        function ensureDetector() {
            if (det || !Steps) return det;
            det = Steps.create({
                tune: TUNE,
                onStep: function (s) { add(s.t); }
            });
            return det;
        }

        return {
            /** Сэмпл датчика: t — мс, a — м/с² (с гравитацией). */
            motion: function (tMs, ax, ay, az) {
                if (typeof ax !== 'number' || typeof ay !== 'number' || typeof az !== 'number') return;
                var t = tMs / 1000;
                if (gT == null || t - gT > 0.5) { gx = ax; gy = ay; gz = az; }
                else {
                    var k = 1 - Math.exp(-(t - gT) * 2 * Math.PI * GRAV_HZ);
                    gx += k * (ax - gx); gy += k * (ay - gy); gz += k * (az - gz);
                }
                gT = t;
                var g = Math.sqrt(gx * gx + gy * gy + gz * gz);
                var nowFlat = g > 0 && Math.abs(gz) / g >= FLAT;
                if (!nowFlat) {
                    if (flat && det) det.gap();
                    flat = false;
                    return;
                }
                flat = true;
                var d = ensureDetector();
                if (d) d.push(tMs, ax, ay, az);
            },
            /** Лежит ли телефон (по последним сэмплам). */
            flat: function () { return flat; },
            /** Датчик молчал (вкладку прятали, пауза на вдохи) — фильтры с нуля. */
            gap: function () { if (det) det.gap(); },
            /** Нажатие: t — мс. */
            tap: function (tMs) { add(tMs / 1000); },
            count: function () { return times.length; },
            stats: function () { return stats(times, marks, target); },
            reset: function () {
                times = []; marks = [];
                if (det) det.reset();
            }
        };
    }

    /*
     * Итог серии: средний темп, доля компрессий в зоне, ровность.
     * В зоне — компрессия, у которой темп по трём последним интервалам
     * в [100; 120]; первые две компрессии темпа ещё не имеют и не считаются.
     */
    function stats(times, marks, target) {
        target = target || TARGET;
        var n = times.length;
        var iv = [];
        for (var i = 1; i < n; i++) iv.push(times[i] - times[i - 1]);
        var rated = marks.filter(function (r) { return r > 0; });
        var inZone = rated.filter(function (r) { return r >= target[0] && r <= target[1]; }).length;
        var slow = rated.filter(function (r) { return r < target[0]; }).length;
        var fast = rated.filter(function (r) { return r > target[1]; }).length;
        var mean = 0;
        if (iv.length) {
            var sum = 0;
            for (var k = 0; k < iv.length; k++) sum += iv[k];
            mean = sum / iv.length;
        }
        var sd = 0;
        if (iv.length > 1) {
            var acc = 0;
            for (var j = 0; j < iv.length; j++) acc += (iv[j] - mean) * (iv[j] - mean);
            sd = Math.sqrt(acc / (iv.length - 1));
        }
        return {
            n: n,
            rate: mean > 0 ? 60 / mean : 0,
            rateNow: rated.length ? rated[rated.length - 1] : 0,
            zone: rated.length ? inZone / rated.length : 0,
            slow: rated.length ? slow / rated.length : 0,
            fast: rated.length ? fast / rated.length : 0,
            cv: mean > 0 ? sd / mean : 0
        };
    }

    /*
     * Вердикт серии. Засчитано — если ≥ 70 % компрессий в зоне. Причина — одна,
     * та, что исправит человек: «медленно», «быстро», «неровно» или «хорошо».
     */
    function verdict(s) {
        if (!s || s.n < 6) return { ok: false, score: 0, key: 'too_few' };
        var score = s.zone;
        var ok = score >= 0.7;
        var key = 'good';
        if (!ok) {
            if (s.slow >= s.fast && s.slow > 0.3) key = 'slow';
            else if (s.fast > 0.3) key = 'fast';
            else key = 'uneven';
        } else if (s.cv > 0.15) {
            key = 'good_uneven';
        }
        return { ok: ok, score: score, key: key };
    }

    return { create: create, stats: stats, verdict: verdict, TARGET: TARGET, TUNE: TUNE };
}));
